import { useEffect, useMemo, useState } from "react";
import { useResource, formatDate, formatDateTime, titleCase, daysUntil } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { FilterBar, matches } from "../components/FilterBar";
import { EntityForm, Field } from "../components/EntityForm";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { IdCardOverlay } from "../components/IdCard";
import { RateCardEditor } from "../components/RateCardEditor";
import { Modal } from "../components/Modal";
import { Link } from "react-router-dom";
import { EMPLOYEE_DOC_CATEGORIES } from "../lib/formOptions";
import { avatarPair, initials } from "../components/InitialsAvatar";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { api } from "../lib/api";
import { uploadFile, fileViewUrl } from "../lib/files";
import type { CompetencyType, Employee, JobRole, TrainingRecord , Shift, Timesheet } from "../lib/types";

const statusChip = (status: Employee["employment_status"]) =>
    status === "active" ? "pass" : status === "on_leave" ? "warn" : "mute";

const EMPLOYMENT_STATUS = [
    { value: "active", label: "Active" },
    { value: "on_leave", label: "On leave" },
    { value: "inactive", label: "Inactive" },
    { value: "terminated", label: "Terminated" },
];

const EMPLOYMENT_TYPE = [
    { value: "full_time", label: "Full time" },
    { value: "part_time", label: "Part time" },
    { value: "casual", label: "Casual" },
    { value: "contractor", label: "Contractor" },
];

type Lookup = { id: string; name: string };

interface OnboardingAdminView {
    account: {
        userId: string | null; hasLoggedIn: boolean; lastLoginAt: string | null;
        isActive: boolean | null; onboardingComplete: boolean; pendingInvite: boolean; linked: boolean;
    };
    adminInfo: { filled: number; total: number };
    userResponsibilities: { done: number; total: number };
    policies: Array<{ policyGroupId: string; name: string; version: string; required: boolean; acknowledged: boolean; acknowledgedAt: string | null }>;
    documents: Array<{ id: string; type_name: string; expiry_date: string | null; verification_status: string; expiry_status: string }>;
}

const employeeFields: Field[] = [
    { name: "first_name", label: "First name", kind: "text", required: true, half: true },
    { name: "last_name", label: "Last name", kind: "text", required: true, half: true },
    { name: "email", label: "Email", kind: "email", half: true },
    { name: "contact_phone", label: "Phone", kind: "tel", half: true },
    { name: "date_of_birth", label: "Date of birth", kind: "date", half: true,
      help: "Drives the birthday panel on the dashboard." },
    { name: "start_date", label: "Start date", kind: "date", required: true, half: true },
    { name: "employment_type", label: "Employment type", kind: "select", options: EMPLOYMENT_TYPE, half: true },
    { name: "employment_status", label: "Status", kind: "select", options: EMPLOYMENT_STATUS, half: true },
    { name: "default_hourly_rate", label: "Base hourly rate", kind: "number", min: 0, half: true },
    { name: "ahpra_number", label: "AHPRA number", kind: "text", half: true },
    { name: "pay_basis", label: "Paid via", kind: "select", half: true,
      options: [
          { value: "tfn", label: "TFN — employee (PAYG + super)" },
          { value: "abn", label: "ABN — paid gross" },
      ],
      help: "How the money leaves. A casual can be paid via ABN: casual hours, contractor money. Payslips switch on this." },
    { name: "abn", label: "ABN number (if paid via ABN)", kind: "text", half: true,
      help: "No ABN quoted while paid via ABN = 47% withholding (ATO rule)." },
    { name: "super_applies", label: "Super applies", kind: "checkbox",
      help: "Leave ON unless this ABN worker is confirmed exempt — a contractor paid mainly for their labour can still be owed super. TFN employees always accrue super." },
    { name: "super_rate", label: "Super rate (decimal)", kind: "number", min: 0, max: 0.25, half: true,
      help: "0.12 = the 12% super guarantee." },
    { name: "withholding_percent", label: "PAYG withholding estimate (decimal)", kind: "number", min: 0, max: 0.6, half: true,
      help: "e.g. 0.20 = 20%. Estimate only; blank uses 20%. Real payroll follows ATO tables." },
    { name: "suburb", label: "Suburb", kind: "text", half: true },
    { name: "region", label: "Service region", kind: "select", half: true,
      optionsFrom: { path: "/service-regions", addLabel: "Add region" },
      help: "The coverage area this staff member usually works — matches the website's areas." },
    { name: "address", label: "Address", kind: "text", half: true },
    { name: "emergency_contact", label: "Emergency contact", kind: "text", half: true },
    { name: "emergency_phone", label: "Emergency phone", kind: "tel", half: true },
    { name: "notes", label: "Notes", kind: "textarea" },
];

type Dialog =
    | { kind: "create" }
    | { kind: "edit"; employee: Employee }
    | { kind: "deactivate"; employee: Employee }
    | { kind: "reactivate"; employee: Employee }
    | { kind: "delete"; employee: Employee }
    | { kind: "training"; employee: Employee }
    | { kind: "idcard"; employee: Employee }
    | { kind: "ratecard"; employee: Employee }
    | { kind: "document"; employee: Employee }
    | null;

interface EmployeeDocRow {
    id: string;
    category: string;
    note: string | null;
    file_id: string;
    file_name: string;
    uploaded_by_name: string | null;
    created_at: string;
    type_name: string | null;
    expiry_date: string | null;
    verification_status: string;
    expiry_status: "none" | "current" | "expiring" | "expired";
    superseded_at: string | null;
}

const DOC_STATUS_CHIP: Record<EmployeeDocRow["expiry_status"], string> = {
    current: "pass", expiring: "warn", expired: "block", none: "mute",
};

/** The single worst compliance state across a person's current documents. */
const worstDocStatus = (docs: EmployeeDocRow[]): { label: string; chip: string } | null => {
    const current = docs.filter((d) => !d.superseded_at);
    const expired = current.filter((d) => d.expiry_status === "expired").length;
    const expiring = current.filter((d) => d.expiry_status === "expiring").length;
    if (expired) return { label: `${expired} expired document${expired === 1 ? "" : "s"}`, chip: "block" };
    if (expiring) return { label: `${expiring} expiring soon`, chip: "warn" };
    if (current.some((d) => d.expiry_status === "current")) return { label: "Documents current", chip: "pass" };
    return null;
};

/** TCAS00001_BrownAlice_RegisteredNurse — the folder name for this person. */
const fileReferenceOf = (employee: Employee, role?: string): string => {
    const compact = (v: string) => v.replace(/[^A-Za-z0-9]/g, "");
    return [employee.ref_code ?? "TCAS?????",
        `${compact(employee.last_name)}${compact(employee.first_name)}`,
        role ? compact(role) : null].filter(Boolean).join("_");
};

const EmployeeDocumentDialog = ({ employee, onClose, onSaved }: {
    employee: Employee;
    onClose: () => void;
    onSaved: () => void;
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [category, setCategory] = useState(EMPLOYEE_DOC_CATEGORIES[0]!);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const save = async () => {
        if (!file) return;
        setBusy(true);
        setError(null);
        try {
            const stored = await uploadFile(file, "general");
            await api.post("/employee-documents", {
                employeeId: employee.id, fileId: stored.id,
                category, note: note.trim() || undefined,
            });
            onSaved();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Upload failed");
            setBusy(false);
        }
    };

    return (
        <Modal title={`File document — ${employee.full_name}`}
            subtitle="Categories mirror the HR folder template, so the digital file and the folder structure stay in step."
            onClose={onClose}
            footer={<>
                <button className="btn" disabled={busy || !file} onClick={() => void save()}>
                    {busy ? "Filing…" : "File document"}
                </button>
                <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
            </>}>
            {error && <div className="error-banner">{error}</div>}
            <div className="form-grid">
                <div className="field half">
                    <label htmlFor="ed-file">Document (max 10 MB)</label>
                    <input id="ed-file" type="file" accept=".pdf,.doc,.docx,.txt,image/*"
                        onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                </div>
                <div className="field half">
                    <label htmlFor="ed-cat">Category</label>
                    <select id="ed-cat" value={category} onChange={(e) => setCategory(e.target.value)}>
                        {EMPLOYEE_DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div className="field">
                    <label htmlFor="ed-note">Note (optional)</label>
                    <input id="ed-note" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
                </div>
            </div>
        </Modal>
    );
};

export const Employees = () => {
    const { user } = useAuth();
    const canManage = user?.role === "admin" || user?.role === "manager";

    const employees = useResource<Employee[]>("/employees");
    const declineStats = useResource<Array<{ employee_id: string; declines: number }>>(
        canManage ? "/decline-stats" : null);
    const training = useResource<TrainingRecord[]>("/training-records");
    const competencies = useResource<CompetencyType[]>("/competency-types");
    const roles = useResource<JobRole[]>("/roles");
    const employeeRoles = useResource<Array<{ employee_id: string; job_role_id: string }>>("/employee-roles");

    // Managed lists backing the onboarding admin fields (all editable in Settings).
    const teams = useResource<Lookup[]>("/teams");
    const serviceRegions = useResource<Array<{ id: string; name: string }>>("/service-regions");
    const activity = useResource<Array<{ employee_id: string; last_login_at: string | null }>>(
        canManage || user?.role === "supervisor" ? "/employee-activity" : null);
    const lastActive = (id: string): string | null =>
        (activity.data ?? []).find((a) => a.employee_id === id)?.last_login_at ?? null;
    const departments = useResource<Lookup[]>("/departments");
    const branches = useResource<Lookup[]>("/branches");
    const responsibilities = useResource<Lookup[]>("/responsibilities");
    const payTypes = useResource<Lookup[]>("/pay-types");
    const overtimeEligibilities = useResource<Lookup[]>("/overtime-eligibilities");


    const [selected, setSelected] = useState<Employee | null>(null);
    const allShifts = useResource<Shift[]>("/shifts");
    const allTimesheets = useResource<Timesheet[]>(canManage ? "/timesheets" : null);
    const staffFile = useResource<EmployeeDocRow[]>(
        selected ? `/employees/${selected.id}/documents` : null);
    const onboarding = useResource<OnboardingAdminView>(
        selected && canManage ? `/employees/${selected.id}/onboarding` : null);
    const policyPacks = useResource<Lookup[]>(canManage ? "/policy-packs" : null);
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<Record<string, string>>({});
    const [dialog, setDialog] = useState<Dialog>(null);

    // Deep link: /employees?id=… — how a timesheet row or roster cell can
    // point straight at a person's record.
    useEffect(() => {
        const wanted = new URLSearchParams(window.location.search).get("id");
        if (!wanted || selected) return;
        const hit = (employees.data ?? []).find((row) => row.id === wanted);
        if (hit) setSelected(hit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employees.data]);

    const refresh = () => {
        employees.reload();
        training.reload();
        setDialog(null);
        setSelected(null);
    };

    const competencyName = useMemo(() => {
        const map = new Map((competencies.data ?? []).map((c) => [c.id, c.name]));
        return (id: string) => map.get(id) ?? "Unknown competency";
    }, [competencies.data]);

    const rolesFor = (employeeId: string): string[] => {
        const roleIds = (employeeRoles.data ?? []).filter((r) => r.employee_id === employeeId).map((r) => r.job_role_id);
        return (roles.data ?? []).filter((r) => roleIds.includes(r.id)).map((r) => r.name);
    };

    /**
     * Compliance is recomputed here from the same rule the database uses: every
     * mandatory competency for every role held must be current. Nothing is read
     * from a stored flag, because a stored flag drifts.
     */
    const complianceFor = (employeeId: string): { current: number; total: number; expired: string[] } => {
        const records = (training.data ?? []).filter((t) => t.employee_id === employeeId);
        const expired = records
            .filter((t) => t.expiry_date && daysUntil(t.expiry_date) < 0)
            .map((t) => competencyName(t.competency_type_id));
        return { current: records.length - expired.length, total: records.length, expired };
    };

    const filtered = (employees.data ?? []).filter((e) =>
        matches(search, e.full_name, e.email) &&
        (!filters.employment_status || e.employment_status === filters.employment_status) &&
        (!filters.employment_type || e.employment_type === filters.employment_type) &&
        (!filters.region || (e as Employee & { region?: string | null }).region === filters.region)
    );

    const trainingFields: Field[] = [
        { name: "competency_type_id", label: "Competency", kind: "select", required: true,
          options: (competencies.data ?? []).map((c) => ({ value: c.id, label: c.name })) },
        { name: "completion_date", label: "Completed on", kind: "date", required: true, half: true },
        { name: "expiry_date", label: "Expires on", kind: "date", half: true,
          help: "Leave blank for a competency that does not expire." },
        { name: "provider", label: "Training provider", kind: "select", half: true,
          optionsFrom: { path: "/training-providers", addLabel: "Add provider" } },
        { name: "assessor", label: "Assessor", kind: "select", half: true,
          optionsFrom: { path: "/training-assessors", addLabel: "Add assessor" } },
        { name: "course_name", label: "Course name", kind: "select",
          optionsFrom: { path: "/training-courses", addLabel: "Add course" } },
        { name: "evidence_url", label: "Evidence link", kind: "text" },
        { name: "notes", label: "Notes", kind: "textarea" },
    ];

    if (employees.error) return <div className="error-banner">{employees.error}</div>;

    // Employment-information section: searchable dropdowns backed by the managed
    // lists, appended to the base employee fields. Options are data, not hardcoded.
    const opt = (rows: Lookup[] | null | undefined) => (rows ?? []).map((r) => ({ value: r.id, label: r.name }));
    const adminInfoFields: Field[] = [
        { name: "team_id", label: "Team", kind: "searchselect", half: true, options: opt(teams.data) },
        { name: "department_id", label: "Department", kind: "searchselect", half: true, options: opt(departments.data) },
        { name: "branch_id", label: "Branch", kind: "searchselect", half: true, options: opt(branches.data) },
        { name: "responsibility_id", label: "Responsibility", kind: "searchselect", half: true, options: opt(responsibilities.data) },
        { name: "pay_type_id", label: "Pay type", kind: "searchselect", half: true, options: opt(payTypes.data) },
        { name: "overtime_eligibility_id", label: "Overtime eligibility", kind: "searchselect", half: true, options: opt(overtimeEligibilities.data) },
        { name: "primary_job_role_id", label: "Role", kind: "searchselect", half: true, options: (roles.data ?? []).map((r) => ({ value: r.id, label: r.name })) },
        { name: "direct_manager_id", label: "Direct manager", kind: "searchselect", half: true,
          options: (employees.data ?? []).map((e) => ({ value: e.id, label: e.full_name })) },
        { name: "standard_hours", label: "Standard hours (per week)", kind: "number", min: 0, half: true },
    ];
    const formFields: Field[] = [...employeeFields, ...adminInfoFields];

    const dialogs = (
        <>
            {dialog?.kind === "create" && (
                <EntityForm
                    title="Add employee" path="/employees" fields={formFields}
                    subtitle="Creating a record here does not create a sign-in account. An admin adds that separately."
                    onClose={() => setDialog(null)} onSaved={refresh}
                />
            )}
            {dialog?.kind === "edit" && (
                <EntityForm
                    title={`Edit ${dialog.employee.full_name}`} path="/employees"
                    fields={formFields} initial={dialog.employee}
                    onClose={() => setDialog(null)} onSaved={refresh}
                />
            )}
            {dialog?.kind === "idcard" && (
                <IdCardOverlay employeeId={dialog.employee.id} onClose={() => setDialog(null)} />
            )}
            {dialog?.kind === "ratecard" && (
                <RateCardEditor employee={dialog.employee} onClose={() => setDialog(null)} />
            )}
            {dialog?.kind === "document" && (
                <EmployeeDocumentDialog employee={dialog.employee}
                    onClose={() => setDialog(null)}
                    onSaved={() => { setDialog(null); staffFile.reload(); }} />
            )}
            {dialog?.kind === "training" && (
                <EntityForm
                    title={`Add training — ${dialog.employee.full_name}`} path="/training-records"
                    fields={trainingFields} fixed={{ employee_id: dialog.employee.id }}
                    subtitle="Recording a mandatory competency here is what unblocks rostering for roles that require it."
                    onClose={() => setDialog(null)} onSaved={refresh}
                />
            )}
            {dialog?.kind === "deactivate" && (
                <ConfirmDialog
                    title={`Deactivate ${dialog.employee.full_name}?`}
                    message="They will no longer be rosterable. Existing shifts are not cancelled — review the roster separately."
                    warning="The record, its training history and its shift history all stay in place. NDIS record keeping runs to seven years, so nothing is erased."
                    confirmLabel="Deactivate" requireReason reasonLabel="Reason for deactivating"
                    request={{ method: "post", path: `/employees/${dialog.employee.id}/deactivate` }}
                    onClose={() => setDialog(null)} onDone={refresh}
                />
            )}
            {dialog?.kind === "reactivate" && (
                <ConfirmDialog
                    title={`Reactivate ${dialog.employee.full_name}?`}
                    message="They become rosterable again, subject to the usual compliance checks."
                    warning={
                        complianceFor(dialog.employee.id).expired.length > 0
                            ? `Note: ${complianceFor(dialog.employee.id).expired.join(", ")} ${
                                complianceFor(dialog.employee.id).expired.length === 1 ? "is" : "are"
                              } expired. Rostering will still be blocked until renewed.`
                            : undefined
                    }
                    confirmLabel="Reactivate"
                    request={{ method: "post", path: `/employees/${dialog.employee.id}/reactivate` }}
                    onClose={() => setDialog(null)} onDone={refresh}
                />
            )}
            {dialog?.kind === "delete" && (
                <ConfirmDialog
                    title={`Remove ${dialog.employee.full_name} from the list?`}
                    tone="danger"
                    message="This retires the record. It disappears from every list and can no longer be rostered."
                    warning="This is not a permanent erase, and it is not meant to be. Shift history, incidents and training evidence referencing this person remain in the database and in the audit log, because NDIS record keeping requires them for seven years. If you need a genuine deletion for a privacy request, that is a separate process."
                    confirmLabel="Remove from list"
                    request={{ method: "delete", path: `/employees/${dialog.employee.id}` }}
                    onClose={() => setDialog(null)} onDone={refresh}
                />
            )}
        </>
    );

    if (selected) {
        const compliance = complianceFor(selected.id);
        const records = (training.data ?? []).filter((t) => t.employee_id === selected.id);

        return (
            <div>
                {dialogs}
                <div className="toolbar">
                    <button className="btn ghost small" onClick={() => setSelected(null)}>← All employees</button>
                    <div className="spacer" />
                    {(() => {
                        const declined = (declineStats.data ?? []).find((d) => d.employee_id === selected.id);
                        return declined && declined.declines >= 2 ? (
                            <span className="chip warn" title="Declined shifts in the last 90 days — a pattern worth a conversation, not a sanction">
                                {declined.declines} declines / 90d
                            </span>
                        ) : null;
                    })()}
                    <span className={`chip ${statusChip(selected.employment_status)}`}>
                        {titleCase(selected.employment_status)}
                    </span>
                    <button className="btn ghost small" onClick={() => setDialog({ kind: "idcard", employee: selected })}>ID card</button>
                    {canManage && (
                        <>
                            <button className="btn ghost small" onClick={() => setDialog({ kind: "edit", employee: selected })}>Edit</button>
                            <button className="btn ghost small" onClick={() => setDialog({ kind: "ratecard", employee: selected })}>Rate card</button>
                            <button className="btn ghost small" onClick={() => setDialog({ kind: "training", employee: selected })}>Add training</button>
                            {selected.employment_status === "inactive" ? (
                                <button className="btn ghost small" onClick={() => setDialog({ kind: "reactivate", employee: selected })}>Reactivate</button>
                            ) : (
                                <button className="btn ghost small" onClick={() => setDialog({ kind: "deactivate", employee: selected })}>Deactivate</button>
                            )}
                        </>
                    )}
                </div>

                {canManage && onboarding.data && (() => {
                    const ob = onboarding.data;
                    const pct = ob.adminInfo.total ? Math.round((ob.adminInfo.filled / ob.adminInfo.total) * 100) : 0;
                    const inviteAgain = async () => {
                        if (!selected.email) return;
                        await api.post("/invites", { email: selected.email, role: "staff", employeeId: selected.id });
                        onboarding.reload();
                    };
                    const applyPack = async (packId: string) => {
                        if (!packId || !ob.account.userId) return;
                        await api.post(`/users/${ob.account.userId}/apply-policy-pack/${packId}`, {});
                        onboarding.reload();
                    };
                    return (
                        <div className="card" style={{ marginBottom: "1rem" }}>
                            <div className="card-head"><h2>Onboarding</h2></div>
                            <div className="card-body">
                                <div className="onboard-status-grid">
                                    <div>
                                        <div className="onboard-meter"><span>Employee information {ob.adminInfo.filled}/{ob.adminInfo.total}</span></div>
                                        <div className="onboard-meter-bar"><i style={{ width: `${pct}%` }} /></div>
                                    </div>
                                    <dl className="kv">
                                        <dt>Login</dt>
                                        <dd>{ob.account.hasLoggedIn ? "Logged in" : (
                                            <>Not logged in{" "}
                                                {selected.email && <button className="btn ghost small" onClick={inviteAgain}>Invite again</button>}</>
                                        )}</dd>
                                        <dt>User responsibilities</dt><dd>{ob.userResponsibilities.done}/{ob.userResponsibilities.total}</dd>
                                        <dt>Onboarding</dt>
                                        <dd>{ob.account.onboardingComplete
                                            ? <span className="chip pass">Complete</span>
                                            : <span className="chip warn">In progress</span>}</dd>
                                    </dl>
                                </div>

                                {ob.policies.length > 0 && (
                                    <table style={{ marginTop: "0.8rem", width: "100%" }}>
                                        <thead><tr><th style={{ textAlign: "left" }}>Policy</th><th>Version</th><th>Status</th></tr></thead>
                                        <tbody>
                                            {ob.policies.map((p) => (
                                                <tr key={p.policyGroupId}>
                                                    <td>{p.name}{p.required ? "" : " (optional)"}</td>
                                                    <td style={{ textAlign: "center" }}>v{p.version}</td>
                                                    <td style={{ textAlign: "center" }}>{p.acknowledged
                                                        ? <span className="chip pass">Acknowledged{p.acknowledgedAt ? ` ${formatDate(p.acknowledgedAt)}` : ""}</span>
                                                        : <span className="chip warn">Pending</span>}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}

                                {(policyPacks.data ?? []).length > 0 && ob.account.userId && (
                                    <div style={{ marginTop: "0.8rem", display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                                        <span className="muted">Assign policy pack:</span>
                                        <select defaultValue="" onChange={(e) => { const v = e.target.value; e.currentTarget.value = ""; if (v) void applyPack(v); }}>
                                            <option value="">Choose a pack…</option>
                                            {(policyPacks.data ?? []).map((pk) => <option key={pk.id} value={pk.id}>{pk.name}</option>)}
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })()}

                <div className="grid cols-2" style={{ marginBottom: "1rem" }}>
                    <div className="card">
                        <div className="card-head"><h2>{selected.full_name}</h2></div>
                        <div className="card-body">
                            <div style={{ display: "flex", gap: "0.9rem", alignItems: "center", marginBottom: "0.8rem" }}>
                                <div className="profile-photo">
                                    {selected.photo_file_id
                                        ? <img src={fileViewUrl(selected.photo_file_id)} alt={`Photo of ${selected.full_name}`} />
                                        : <span style={{ background: avatarPair(selected.full_name)[0], color: avatarPair(selected.full_name)[1] }}>{initials(selected.full_name)}</span>}
                                </div>
                                {canManage && (
                                    <label className="btn ghost small" style={{ cursor: "pointer" }}>
                                        {selected.photo_file_id ? "Change photo" : "Upload photo"}
                                        <input type="file" accept="image/jpeg,image/png,image/webp" hidden
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (!file) return;
                                                void (async () => {
                                                    const stored = await uploadFile(file, "general");
                                                    await api.put(`/employees/${selected.id}`, { photo_file_id: stored.id });
                                                    employees.reload();
                                                })();
                                            }} />
                                    </label>
                                )}
                            </div>
                            <dl className="kv">
                                <dt>File reference</dt>
                                <dd className="mono">{fileReferenceOf(selected, rolesFor(selected.id)[0])}</dd>
                                <dt>Email</dt><dd>{selected.email ?? "—"}</dd>
                                <dt>Phone</dt><dd>{selected.contact_phone ?? "—"}</dd>
                                <dt>Employment</dt><dd>{titleCase(selected.employment_type)}</dd>
                                <dt>Started</dt><dd>{formatDate(selected.start_date)}</dd>
                                <dt>Base rate</dt><dd>${selected.default_hourly_rate}/hr</dd>
                                <dt>Roles</dt>
                                <dd>
                                    {rolesFor(selected.id).length > 0
                                        ? rolesFor(selected.id).map((r) => (
                                            <span key={r} className="chip info" style={{ marginRight: 4 }}>{r}</span>
                                          ))
                                        : "—"}
                                </dd>
                            </dl>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-head">
                            <h2>Compliance</h2>
                            <span className={`chip ${compliance.expired.length === 0 ? "pass" : "block"}`}>
                                {compliance.expired.length === 0 ? "compliant" : `${compliance.expired.length} expired`}
                            </span>
                        </div>
                        <div className="card-body">
                            {compliance.expired.length > 0 ? (
                                <p style={{ color: "var(--block)", fontSize: "0.85rem" }}>
                                    {compliance.expired.join(", ")} — rostering is blocked for any role requiring these.
                                </p>
                            ) : (
                                <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>
                                    All {compliance.total} recorded competencies are current.
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-head">
                            <h2>Roster &amp; pay</h2>
                            <Link to="/rostering" style={{ fontSize: "0.78rem", fontWeight: 600 }}>Open rostering →</Link>
                        </div>
                        <div className="card-body" style={{ display: "grid", gap: "0.6rem" }}>
                            {(() => {
                                const theirs = (allShifts.data ?? [])
                                    .filter((sh) => sh.employee_id === selected.id && sh.status !== "cancelled");
                                const upcoming = theirs
                                    .filter((sh) => new Date(sh.ends_at) >= new Date())
                                    .sort((a, b) => a.starts_at.localeCompare(b.starts_at))
                                    .slice(0, 4);
                                const theirSheets = (allTimesheets.data ?? [])
                                    .filter((t) => t.employee_id === selected.id);
                                return (
                                    <>
                                        {upcoming.length === 0
                                            ? <p style={{ color: "var(--muted)", fontSize: "0.83rem" }}>Nothing rostered ahead.</p>
                                            : upcoming.map((sh) => (
                                                <div key={sh.id} style={{ display: "flex", justifyContent: "space-between", gap: "0.6rem", fontSize: "0.83rem" }}>
                                                    <span>
                                                        {sh.participant_id ? (
                                                            <Link to={`/participants?id=${sh.participant_id}`}>{sh.placement_name}</Link>
                                                        ) : (sh.placement_name ?? "—")}
                                                    </span>
                                                    <span className="mono" style={{ whiteSpace: "nowrap" }}>{formatDate(sh.starts_at)}</span>
                                                </div>
                                            ))}
                                        {canManage && (
                                            <p style={{ fontSize: "0.78rem", color: "var(--muted)", borderTop: "1px solid var(--line-soft)", paddingTop: "0.5rem" }}>
                                                {theirSheets.length} timesheet{theirSheets.length === 1 ? "" : "s"} on record
                                                {theirSheets[0] && ` — latest ${theirSheets[0].status}`}.{" "}
                                                <Link to="/crm/timesheets">Open timesheets →</Link>
                                            </p>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-head">
                        <h2>Training &amp; competency</h2>
                        {canManage && (
                            <button className="btn small" onClick={() => setDialog({ kind: "training", employee: selected })}>
                                + Add training
                            </button>
                        )}
                    </div>
                    <div className="card-body flush">
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr><th>Competency</th><th>Provider</th><th>Completed</th><th>Expires</th><th>Status</th></tr>
                                </thead>
                                <tbody>
                                    {records.map((record) => {
                                        const days = record.expiry_date ? daysUntil(record.expiry_date) : null;
                                        const chip = days === null ? "pass" : days < 0 ? "block" : days <= 30 ? "warn" : "pass";
                                        const label = days === null ? "no expiry" : days < 0 ? "expired" : days <= 30 ? "due soon" : "current";
                                        return (
                                            <tr key={record.id}>
                                                <td>{competencyName(record.competency_type_id)}</td>
                                                <td>{record.provider ?? "—"}</td>
                                                <td className="mono">{formatDate(record.completion_date)}</td>
                                                <td className="mono">{formatDate(record.expiry_date)}</td>
                                                <td><span className={`chip ${chip}`}>{label}</span></td>
                                            </tr>
                                        );
                                    })}
                                    {records.length === 0 && (
                                        <tr><td colSpan={5} className="empty">No training recorded.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="card" style={{ marginTop: "1rem" }}>
                    <div className="card-head">
                        <div>
                            <h2>Staff file{(() => {
                                const w = worstDocStatus(staffFile.data ?? []);
                                return w ? <span className={`chip ${w.chip}`} style={{ marginLeft: 8, fontSize: "0.7rem" }}>{w.label}</span> : null;
                            })()}</h2>
                            <p style={{ fontSize: "0.76rem", color: "var(--muted)", marginTop: "0.15rem" }}>
                                Filed under {fileReferenceOf(selected, rolesFor(selected.id)[0])} — same
                                structure as the HR folder template.
                            </p>
                        </div>
                        {canManage && (
                            <button className="btn small" onClick={() => setDialog({ kind: "document", employee: selected })}>
                                + File document
                            </button>
                        )}
                    </div>
                    <div className="card-body flush">
                        {staffFile.error && <div className="error-banner" style={{ margin: "0.75rem" }}>{staffFile.error}</div>}
                        <div className="table-wrap">
                            <table>
                                <thead><tr><th>Category</th><th>Status</th><th>File</th><th>Note</th><th>Filed by</th><th>Added</th>
                                    {canManage && <th style={{ textAlign: "right" }}>Actions</th>}</tr></thead>
                                <tbody>
                                    {(staffFile.data ?? []).map((doc) => (
                                        <tr key={doc.id} style={{ opacity: doc.superseded_at ? 0.55 : 1 }}>
                                            <td><span className="chip info">{doc.category}</span></td>
                                            <td>
                                                {doc.expiry_date
                                                    ? <span className={`chip ${DOC_STATUS_CHIP[doc.expiry_status]}`}>{doc.expiry_status}</span>
                                                    : <span className="muted">—</span>}
                                                {doc.superseded_at && <span className="chip mute" style={{ marginLeft: 4 }}>superseded</span>}
                                            </td>
                                            <td>
                                                <a href={fileViewUrl(doc.file_id)} target="_blank" rel="noreferrer" style={{ fontWeight: 600 }}>
                                                    {doc.file_name}
                                                </a>
                                            </td>
                                            <td style={{ color: "var(--ink-soft)" }}>{doc.note ?? "—"}</td>
                                            <td style={{ fontSize: "0.8rem" }}>{doc.uploaded_by_name ?? "—"}</td>
                                            <td className="mono">{formatDate(doc.created_at)}</td>
                                            {canManage && (
                                                <td><div className="row-actions">
                                                    <button className="icon-btn danger" onClick={async () => {
                                                        await api.delete(`/employee-documents/${doc.id}`);
                                                        staffFile.reload();
                                                    }}>Remove</button>
                                                </div></td>
                                            )}
                                        </tr>
                                    ))}
                                    {(staffFile.data ?? []).length === 0 && (
                                        <tr><td colSpan={canManage ? 7 : 6} className="empty">
                                            Nothing filed yet. Everything from application to termination
                                            lives here, in the same categories as the folder template.
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            {dialogs}

            <FilterBar
                search={search} onSearch={setSearch}
                searchPlaceholder="Search employees…"
                values={filters}
                onChange={(name, value) => setFilters((f) => ({ ...f, [name]: value }))}
                filters={[
                    { name: "employment_status", allLabel: "All Status", options: EMPLOYMENT_STATUS },
                    { name: "employment_type", allLabel: "All Types", options: EMPLOYMENT_TYPE },
                    { name: "region", allLabel: "All Regions",
                      options: (serviceRegions.data ?? []).map((r) => ({ value: r.name, label: r.name })) },
                ]}
                resultNote={
                    filtered.length !== (employees.data?.length ?? 0)
                        ? `Showing ${filtered.length} of ${employees.data?.length ?? 0} employees`
                        : undefined
                }
                action={canManage && (
                    <>
                        <button className="btn ghost" onClick={() => {
                            // Real CSV export of the filtered list (the prototype's was a stub).
                            const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
                            const rows = filtered.map((e) => {
                                const teamId = (e as Employee & { team_id?: string | null }).team_id;
                                const team = teamId ? (teams.data ?? []).find((t) => t.id === teamId)?.name ?? "" : "";
                                const comp = complianceFor(e.id);
                                return [e.full_name, e.email ?? "", rolesFor(e.id).join("; "), team,
                                    e.contact_phone ?? "", e.employment_type, e.employment_status,
                                    comp.expired.length ? `${comp.expired.length} expired` : "current"].map(esc).join(",");
                            });
                            const csv = ["Name,Email,Roles,Team,Mobile,Employment,Status,Compliance", ...rows].join("\r\n");
                            const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
                            const a = document.createElement("a");
                            a.href = url; a.download = "employees.csv"; a.click();
                            URL.revokeObjectURL(url);
                        }}>⤓ Export</button>
                        <button className="btn" onClick={() => setDialog({ kind: "create" })}>+ Add Employee</button>
                    </>
                )}
            />

            <div className="card">
                <div className="card-body flush">
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Name</th><th>Roles</th><th>Team</th><th>Mobile</th><th>Employment</th>
                                    <th>Status</th><th>Compliance</th><th>Last active</th>
                                    {canManage && <th style={{ textAlign: "right" }}>Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((employee) => {
                                    const compliance = complianceFor(employee.id);
                                    return (
                                        <tr key={employee.id} className="clickable" onClick={() => setSelected(employee)}>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                                                    <InitialsAvatar name={employee.full_name} size={28} />
                                                    <div>
                                                        <div style={{ fontWeight: 600 }}>{employee.full_name}</div>
                                                        <div style={{ color: "var(--muted)", fontSize: "0.75rem" }}>
                                                            {employee.ref_code && <span className="mono">{employee.ref_code} · </span>}
                                                            {employee.email}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {rolesFor(employee.id).map((r) => (
                                                    <span key={r} className="chip info" style={{ marginRight: 3 }}>{r}</span>
                                                ))}
                                            </td>
                                            <td>{(() => {
                                                const teamId = (employee as Employee & { team_id?: string | null }).team_id;
                                                return teamId ? (teams.data ?? []).find((t) => t.id === teamId)?.name ?? "—" : "—";
                                            })()}</td>
                                            <td className="mono">{employee.contact_phone ?? "—"}</td>
                                            <td>{titleCase(employee.employment_type)}</td>
                                            <td>
                                                <span className={`chip ${statusChip(employee.employment_status)}`}>
                                                    {titleCase(employee.employment_status)}
                                                </span>
                                            </td>
                                            <td>
                                                {compliance.expired.length === 0 ? (
                                                    <span className="chip pass">current</span>
                                                ) : (
                                                    <span className="chip block">{compliance.expired.length} expired</span>
                                                )}
                                            </td>
                                            <td className="mono" style={{ fontSize: "0.78rem" }}>
                                                {lastActive(employee.id) ? formatDateTime(lastActive(employee.id)!) : "—"}
                                            </td>
                                            {canManage && (
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    <div className="row-actions">
                                                        <button className="icon-btn" onClick={() => setDialog({ kind: "edit", employee })}>Edit</button>
                                                        <button className="icon-btn" onClick={() => setDialog({ kind: "training", employee })}>Training</button>
                                                        {employee.employment_status === "inactive" ? (
                                                            <button className="icon-btn" onClick={() => setDialog({ kind: "reactivate", employee })}>Reactivate</button>
                                                        ) : (
                                                            <button className="icon-btn" onClick={() => setDialog({ kind: "deactivate", employee })}>Deactivate</button>
                                                        )}
                                                        <button className="icon-btn danger" onClick={() => setDialog({ kind: "delete", employee })}>Remove</button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={canManage ? 9 : 8} className="empty">No employees match.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};
